// admin/src/pages/ScimIntegrations/scimOutboundApi.js

import { request } from '../../utils/request';

const OUTBOUND_BASE_PATH = 'admin/scim/outbound';

const DEFAULT_FETCH_CONFIG = {
  cache: 'no-store',
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
};

/**
 * Normalizes outbound SCIM backend error responses into fixed human-friendly messages.
 * Never outputs raw server stack or exception text.
 *
 * @param {any} err
 * @returns {string}
 */
export function extractOutboundErrorMessage(err) {
  if (!err) return 'An unexpected error occurred.';

  const status = err.status || err.statusCode || err.response?.status;
  const msg = String(err.message || err.error || err.response?.data?.message || err.response?.data?.error || '');

  if (status === 401 || msg.toLowerCase().includes('unauthorized') || msg.toLowerCase().includes('authentication required')) {
    return 'Your session has expired. Please log in again.';
  }

  if (
    status === 403 ||
    msg.toLowerCase().includes('missing required permission') ||
    msg.toLowerCase().includes('access denied') ||
    msg.toLowerCase().includes('forbidden')
  ) {
    return 'Access denied. You do not have permission to perform this action.';
  }

  if (status === 404 || msg.toLowerCase().includes('disabled') || msg.toLowerCase().includes('scim outbound')) {
    return 'Outbound provisioning is not enabled on the server';
  }

  if (status === 409 || msg.toLowerCase().includes('sync is already running')) {
    return 'A sync is already running or the data changed. Please refresh and try again.';
  }

  if (msg.toLowerCase().includes('confirmall') || msg.toLowerCase().includes('confirmation')) {
    return 'Scope type "all" requires explicit confirmAll: true confirmation.';
  }

  if (
    msg.toLowerCase().includes('unreachable') ||
    msg.toLowerCase().includes('blocked by security policy') ||
    msg.toLowerCase().includes('econnrefused')
  ) {
    return 'Target unreachable or blocked by security policy.';
  }

  if (status === 429) {
    return 'Too many requests. Please wait a moment before trying again.';
  }

  if (
    status === 503 ||
    msg.toLowerCase().includes('unavailable') ||
    msg.toLowerCase().includes('configuration error')
  ) {
    return 'Outbound SCIM configuration error, contact an administrator';
  }

  return 'Operation failed. Please try again.';
}

/**
 * List paginated outbound targets
 */
export async function listOutboundTargets({ page = 1, limit = 10, status } = {}, signal) {
  const query = { page, limit };
  if (status && status !== 'all') query.status = status;

  return request(`${OUTBOUND_BASE_PATH}/targets`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get target by ID
 */
export async function getOutboundTarget(id, signal) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Create a new outbound target (token is write-only)
 */
export async function createOutboundTarget(payload) {
  return request(`${OUTBOUND_BASE_PATH}/targets`, {
    method: 'POST',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Update outbound target configuration (excluding token)
 */
export async function updateOutboundTarget(id, payload) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}`, {
    method: 'PUT',
    body: payload,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Toggle target status (active or disabled)
 */
export async function updateOutboundTargetStatus(id, status) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/status`, {
    method: 'PATCH',
    body: { status },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Rotate target bearer token (write-only)
 */
export async function rotateOutboundTargetToken(id, token) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/rotate-token`, {
    method: 'POST',
    body: { token },
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Delete target and associated mappings
 */
export async function deleteOutboundTarget(id) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}`, {
    method: 'DELETE',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Test target connectivity and capability
 */
export async function testOutboundTarget(id) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/test`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Trigger manual synchronization cycle
 */
export async function syncOutboundTarget(id) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/sync`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Get target status and windowed metrics (24h and 7d)
 */
export async function getOutboundTargetStatus(id, signal) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/status`, {
    method: 'GET',
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List paginated target user mappings
 */
export async function listOutboundTargetMappings(id, { page = 1, limit = 20, state } = {}, signal) {
  const query = { page, limit };
  if (state && state !== 'all') query.state = state;

  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/mappings`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Retry a single mapping
 */
export async function retryOutboundTargetMapping(id, mappingId) {
  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/mappings/${mappingId}/retry`, {
    method: 'POST',
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * List paginated target activity log entries
 */
export async function listOutboundTargetActivity(id, { page = 1, limit = 20, operation, outcome } = {}, signal) {
  const query = { page, limit };
  if (operation && operation !== 'all') query.operation = operation;
  if (outcome && outcome !== 'all') query.outcome = outcome;

  return request(`${OUTBOUND_BASE_PATH}/targets/${id}/activity`, {
    method: 'GET',
    query,
    signal,
    cache: DEFAULT_FETCH_CONFIG.cache,
    headers: DEFAULT_FETCH_CONFIG.headers,
  });
}

/**
 * Fetch existing groups for scope assignment (read-only)
 * Uses the exact same call the app already uses in groups.js
 */
export async function fetchGroupsList(signal) {
  return request('groups', {
    method: 'GET',
    signal,
  });
}

/**
 * Fetch existing users for scope assignment (read-only)
 * Uses the existing users API the admin app already uses in users.js / UserManagement.js
 */
export async function fetchUsersList(signal) {
  try {
    const res = await request('users', {
      method: 'GET',
      signal,
    });
    return Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
  } catch {
    try {
      const res2 = await request('user', {
        method: 'GET',
        signal,
      });
      return Array.isArray(res2) ? res2 : Array.isArray(res2?.data) ? res2.data : [];
    } catch {
      return [];
    }
  }
}
